import React, { useState } from 'react';
import {
    Cookie,
    Settings,
    Analytics,
    Campaign as Ads,
    Security,
    ToggleOn,
    ToggleOff,
    Save
} from '@mui/icons-material';

const CookiePolicyPage = () => {
    const lastUpdated = "August 1, 2025";
    const [cookieSettings, setCookieSettings] = useState({
        essential: true, // Always true, can't be disabled
        analytics: true,
        marketing: false,
        preferences: true
    });

    const handleToggle = (type) => {
        if (type === 'essential') return; // Can't disable essential cookies
        setCookieSettings(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const saveCookieSettings = () => {
        // In a real app, this would save to localStorage or send to server
        console.log('Saving cookie settings:', cookieSettings);
        alert('Cookie preferences saved successfully!');
    };

    const cookieTypes = [
        {
            type: 'essential',
            icon: <Security style={{ fontSize: 24, color: '#16a34a' }} />,
            title: 'Essential Cookies',
            description: 'Required for basic website functionality, security, and user authentication.',
            examples: ['Login sessions', 'Security tokens', 'Load balancing'],
            duration: 'Session or up to 1 year',
            canDisable: false
        },
        {
            type: 'analytics',
            icon: <Analytics style={{ fontSize: 24, color: '#2563eb' }} />,
            title: 'Analytics Cookies',
            description: 'Help us understand how visitors use our website to improve user experience.',
            examples: ['Google Analytics', 'Usage statistics', 'Performance metrics'],
            duration: 'Up to 2 years',
            canDisable: true
        },
        {
            type: 'preferences',
            icon: <Settings style={{ fontSize: 24, color: '#7c3aed' }} />,
            title: 'Preference Cookies',
            description: 'Remember your choices and settings to provide a personalized experience.',
            examples: ['Language settings', 'Theme preferences', 'Dashboard layout'],
            duration: 'Up to 1 year',
            canDisable: true
        },
        {
            type: 'marketing',
            icon: <Ads style={{ fontSize: 24, color: '#dc2626' }} />,
            title: 'Marketing Cookies',
            description: 'Used to deliver relevant advertising and measure campaign effectiveness.',
            examples: ['Ad targeting', 'Conversion tracking', 'Social media pixels'],
            duration: 'Up to 1 year',
            canDisable: true
        }
    ];

    const sections = [
        {
            title: "What Are Cookies?",
            content: [
                "Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.",
                "Cookies contain information that is transferred to your device's hard drive. They help us recognize your device and remember information about your visit, such as your preferred language and other settings."
            ]
        },
        {
            title: "How We Use Cookies",
            content: [
                "We use cookies to:",
                "• Ensure our website functions properly and securely",
                "• Remember your login status and preferences",
                "• Analyze how our website is used and improve performance",
                "• Provide personalized content and recommendations",
                "• Measure the effectiveness of our marketing campaigns",
                "• Prevent fraud and enhance security"
            ]
        },
        {
            title: "Third-Party Cookies",
            content: [
                "We may use third-party services that set their own cookies on your device:",
                "Google Analytics: Helps us understand website usage and visitor behavior",
                "Social Media Platforms: Enable social sharing and embedded content",
                "Customer Support Tools: Provide chat support and help desk functionality",
                "Payment Processors: Handle secure payment transactions",
                "These third parties have their own privacy policies governing their use of cookies."
            ]
        },
        {
            title: "Managing Your Cookie Preferences",
            content: [
                "You can control and manage cookies in several ways:",
                "Browser Settings: Most browsers allow you to control cookies through their settings. You can typically find these options in the 'Settings' or 'Preferences' menu.",
                "Cookie Consent Tool: Use our cookie preference center above to enable or disable non-essential cookies.",
                "Opt-out Links: Some third-party services provide direct opt-out links for their cookies.",
                "Note that disabling certain cookies may affect the functionality of our website."
            ]
        },
        {
            title: "Cookie Retention",
            content: [
                "Different cookies have different lifespans:",
                "Session Cookies: Deleted when you close your browser",
                "Persistent Cookies: Remain on your device for a set period or until manually deleted",
                "The specific duration for each type of cookie is listed in the cookie types section above."
            ]
        },
        {
            title: "Updates to This Policy",
            content: [
                "We may update this Cookie Policy from time to time to reflect changes in our practices or legal requirements.",
                "When we make changes, we will update the 'Last Updated' date at the top of this policy.",
                "We encourage you to review this policy periodically to stay informed about how we use cookies."
            ]
        }
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            {/* Header Spacer */}
            <div style={{ height: '64px' }}></div>

            {/* Hero Section */}
            <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eff6ff 100%)' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <Cookie style={{ fontSize: 48, color: '#2563eb' }} />
                    </div>
                    <h1 style={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        marginBottom: '24px',
                        color: '#111827'
                    }}>
                        Cookie Policy
                    </h1>
                    <p style={{
                        fontSize: '20px',
                        color: '#6b7280',
                        maxWidth: '800px',
                        margin: '0 auto',
                        lineHeight: '1.6'
                    }}>
                        Learn about how RiskAS uses cookies to improve your experience and
                        how you can control your cookie preferences.
                    </p>
                    <div style={{
                        marginTop: '32px',
                        padding: '16px 24px',
                        background: 'rgba(37, 99, 235, 0.1)',
                        borderRadius: '8px',
                        display: 'inline-block'
                    }}>
                        <span style={{ color: '#2563eb', fontWeight: '600' }}>
                            Last updated: {lastUpdated}
                        </span>
                    </div>
                </div>
            </section>

            {/* Cookie Preference Center */}
            <section style={{ padding: '60px 0', background: 'white' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 16px' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', textAlign: 'center' }}>
                        Cookie Preference Center
                    </h2>
                    <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '32px' }}>
                        Manage your cookie preferences below. Some cookies are essential for our website to function properly.
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {cookieTypes.map((cookie, index) => (
                            <div key={index} style={{
                                background: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '24px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                                            {cookie.icon}
                                            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginLeft: '12px' }}>
                                                {cookie.title}
                                            </h3>
                                        </div>
                                        <p style={{ color: '#6b7280', marginBottom: '16px', lineHeight: '1.6' }}>
                                            {cookie.description}
                                        </p>
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong style={{ color: '#374151', fontSize: '14px' }}>Examples:</strong>
                                            <span style={{ color: '#6b7280', fontSize: '14px', marginLeft: '8px' }}>
                                                {cookie.examples.join(', ')}
                                            </span>
                                        </div>
                                        <div>
                                            <strong style={{ color: '#374151', fontSize: '14px' }}>Duration:</strong>
                                            <span style={{ color: '#6b7280', fontSize: '14px', marginLeft: '8px' }}>
                                                {cookie.duration}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ marginLeft: '24px' }}>
                                        <button
                                            onClick={() => handleToggle(cookie.type)}
                                            disabled={!cookie.canDisable}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: cookie.canDisable ? 'pointer' : 'not-allowed',
                                                opacity: cookie.canDisable ? 1 : 0.5
                                            }}
                                        >
                                            {cookieSettings[cookie.type] ? (
                                                <ToggleOn style={{ fontSize: 32, color: '#16a34a' }} />
                                            ) : (
                                                <ToggleOff style={{ fontSize: 32, color: '#6b7280' }} />
                                            )}
                                        </button>
                                        {!cookie.canDisable && (
                                            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>
                                                Always Active
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                        <button
                            onClick={saveCookieSettings}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <Save style={{ fontSize: 20 }} />
                            Save Preferences
                        </button>
                    </div>
                </div>
            </section>

            {/* Cookie Policy Content */}
            <section style={{ padding: '80px 0', background: '#f9fafb' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb'
                    }}>
                        {sections.map((section, index) => (
                            <div key={index} style={{
                                padding: '32px',
                                borderBottom: index < sections.length - 1 ? '1px solid #f3f4f6' : 'none'
                            }}>
                                <h2 style={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: '#111827',
                                    marginBottom: '16px'
                                }}>
                                    {section.title}
                                </h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {section.content.map((paragraph, pIndex) => (
                                        <p key={pIndex} style={{
                                            color: '#374151',
                                            lineHeight: '1.6',
                                            fontSize: '16px',
                                            margin: 0
                                        }}>
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Browser Settings Guide */}
            <section style={{ padding: '60px 0', background: 'white' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 16px' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', textAlign: 'center' }}>
                        Browser Cookie Settings
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                                Chrome
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5', marginBottom: '12px' }}>
                                Settings → Privacy and Security → Cookies and other site data
                            </p>
                            <button
                                onClick={() => window.open('https://support.google.com/chrome/answer/95647', '_blank')}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Learn More
                            </button>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                                Firefox
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5', marginBottom: '12px' }}>
                                Settings → Privacy & Security → Cookies and Site Data
                            </p>
                            <button
                                onClick={() => window.open('https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop', '_blank')}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Learn More
                            </button>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                                Safari
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5', marginBottom: '12px' }}>
                                Preferences → Privacy → Manage Website Data
                            </p>
                            <button
                                onClick={() => window.open('https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac', '_blank')}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Learn More
                            </button>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
                                Edge
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5', marginBottom: '12px' }}>
                                Settings → Cookies and site permissions → Cookies and site data
                            </p>
                            <button
                                onClick={() => window.open('https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09', '_blank')}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Learn More
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section style={{ padding: '60px 0', background: '#f9fafb' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                        Questions About Cookies?
                    </h2>
                    <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px' }}>
                        If you have any questions about our use of cookies or this Cookie Policy, please contact us.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button
                            onClick={() => window.location.href = '/contact'}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            Contact Us
                        </button>
                        <button
                            onClick={() => window.location.href = '/privacy-policy'}
                            style={{
                                background: 'white',
                                color: '#2563eb',
                                border: '2px solid #2563eb',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Privacy Policy
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CookiePolicyPage;